import { useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Typography, Box, IconButton,
  Button, Tooltip, useTheme, useMediaQuery, Grid, TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import { useReactToPrint } from 'react-to-print';
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../../../../lib/axios';
import RouteIcon from '@mui/icons-material/Route';
import dayjs from 'dayjs';

const fixImageUrl = (url) => {
  if (!url) return null;
  return url.replace(/^http:\/([^/])/, 'http://$1');
};

const TicketDetailsPopup = ({ open, onClose, ticket }) => {
  const theme = useTheme();
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [followUpText, setFollowUpText] = useState("");
  const [UserId, setUserId] = useState(sessionStorage.getItem('UserId'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });
  const [formData, setFormData] = useState({
    ToDo: "",
    Remark: ""
  })

  if (!ticket) return null;
  const imageUrl = fixImageUrl(ticket.ImgName);
  const hasImage = !!imageUrl
  const dateObj = new Date(ticket.TktDate);
  const date = dateObj.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const time = dateObj.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const statusColors = {
    O: { label: 'OPEN', bg: '#e8f5e9', color: '#2e7d32' },
    I: { label: 'IN PROGRESS', bg: '#fff8e1', color: '#f57c00' },
    C: { label: 'CLOSED', bg: '#ffebee', color: '#d32f2f' },
    R: { label: 'RESOLVED', bg: '#e8f5ff', color: '#1976d2' },
    H: { label: 'Hold', bg: '#e8f5ff', color: '#800080' },
  };

  const status = statusColors[ticket.TicketSt] || {
    label: 'UNKNOWN',
    bg: '#f5f5f5',
    color: '#616161',
  };

  const locationValue = ticket.RoomBedName || ticket.AmenityName || ticket.CommonAreaName || ticket.FloorName || 'N/A';
  const locationLabel = ticket.RoomBedName ? 'Bed' : ticket.AmenityName ? 'Amenity' : ticket.CommonAreaName ? 'Common Area' : ticket.FloorName ? 'Floor' : 'Location';

  const handleFollowUpClick = () => {
    setFollowUpDialogOpen(true);
  };

  const handleFollowUpCancel = () => {
    setFollowUpDialogOpen(false);
    setFormData({
      Todo: '',
      Remark: ''
    })
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  };

  const handleSubmit = async () => {
    try {
      const currentDate = dayjs().format('YYYY-MM-DD');
      const response = await axiosInstance.post('TrnTktFlw/InsertTrnTktFlw', {
        FlwDt: currentDate,
        TicketId: ticket.TicketId,
        ToDo: formData.ToDo,
        Remark: formData.Remark,
        FlwUserId: UserId,
        FlwResidentId: ticket.ResidentId,
        CreatedBy: 1,
        TktFlwImage: "",
        ImgName: ""
      });
      if (response.data.status === 0 && response.data.ResponseStatusCode === 1) {
        toast.success(response.data.Message || 'Ticket Followed up successfully.');
        setFormData({
          ToDo: "",
          Remark: ""
        });
      }
    } catch (error) {
      toast.error("Error while doing follow up.");
    }
    setFollowUpDialogOpen(false);
  };

  return (
    <>
      <ToastContainer />
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: '#fff',
            borderBottom: '1px solid #e0e0e0',
            px: 3,
            py: 1,
          }}
        >
          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
            <Button variant='outlined' size='small' onClick={handleFollowUpClick} sx={{ textTransform: 'none' }} disabled={ticket.TicketSt === "R"}>
              Follow Up
            </Button>
            <Tooltip title="Print Ticket" arrow>
              <Button variant="outlined" onClick={() => reactToPrintFn()} size="small">
                <PrintIcon size='small' />
              </Button>
            </Tooltip>
            <IconButton onClick={onClose}>
              <CloseIcon color="error" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Box ref={contentRef}>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: '#635bff',
              mb: -2,
              '@media print': {
                pt: 6,
                mb: 2,
              },
            }}
          >
            Service/Complain Ticket Details
          </Typography>
          <DialogContent>
            <Box mb={0.3} p={0.2} border="1px solid #e0e0e0" borderRadius={1}>
              <Typography
                variant="h6"
                color="primary"
                sx={{
                  borderBottom: '2px solid #635bff',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  pl: 2,
                }}
              >
                Ticket Information
              </Typography>
              <Box pl={2} mt={1}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    '@media print': {
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'nowrap',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                    <strong>Ticket No:</strong>{' '}
                    <span
                      style={{
                        fontFamily: '"Courier New", monospace',
                        fontWeight: 600,
                        fontSize: '1.1rem',
                      }}
                    >
                      {ticket.TicketNo}
                    </span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      pr: '20px',
                      '@media print': {
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    <strong>Date:</strong> {date} &nbsp;|&nbsp; <strong>Start Time:</strong> {time}
                  </Typography>
                </Box>
                {ticket.TicketServName && (
                  <Box mt={1} sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    '@media print': {
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'nowrap',
                    },
                  }}>
                    <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                      <strong>Priority:</strong> {ticket.TicketServName}
                    </Typography>
                    <Typography
                      variant="h6"
                      color="primary"
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        pl: 2,
                      }}
                    >
                      Status:
                      <Box
                        sx={{
                          display: 'inline-block',
                          // bgcolor: status.bg,
                          color: status.color,
                          px: 4,
                          py: 0.8,
                          borderRadius: 0,
                          fontWeight: 'bold',
                          fontSize: '0.9rem',
                          border: `0px solid ${status.color}33`,
                        }}
                      >
                        {status.label}
                      </Box>
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box mb={0.3} p={0.2} border="1px solid #e0e0e0" borderRadius={1}>
              <Typography
                variant="h6"
                color="primary"
                sx={{
                  borderBottom: '2px solid #635bff',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  pl: 2,
                }}
              >
                Location & Service Details
              </Typography>
              <Box pl={2} mt={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                        <strong>Property:</strong> {ticket.PropName}
                      </Typography>
                      <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                        <strong>Service/Complain:</strong> {ticket.ServiceName || 'N/A'}
                      </Typography>
                      <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                        <strong>Category:</strong> {ticket.CategoryName || 'N/A'}
                      </Typography>
                      <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                        <strong>Subcategory:</strong> {ticket.SubCategoryName || 'N/A'}
                      </Typography>
                      <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                        <strong>{locationLabel}:</strong> {locationValue}
                      </Typography>
                      {/* Additional Details */}
                      {ticket.TktFor && (
                        <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                          <strong>Ticket For:</strong> {ticket.TktFor}
                        </Typography>
                      )}
                      {ticket.TicketTagName && (
                        <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                          <strong>Tag:</strong> {ticket.TicketTagName}
                        </Typography>
                      )}
                      {ticket.LeadTime && (
                        <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                          <strong>Lead Time:</strong> {ticket.LeadTime || '0.00 Min'}
                        </Typography>
                      )}
                      {ticket.DelayTime && ticket.DelayTime !== '0.00 Min' && (
                        <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                          <strong>Delay Time:</strong> {ticket.DelayTime}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Image Section on the right */}
                  <Grid item xs={12} sm={4}>
                    {hasImage && (
                      <Box
                        sx={{
                          width: '100%',
                          height: '190px',
                          borderRadius: 1,
                          overflow: 'hidden',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          boxShadow: 2,
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt="Ticket Image"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>
            <Box mb={0.3} p={0.2} border="1px solid #e0e0e0" borderRadius={1}>
              <Typography
                variant="h6"
                color="primary"
                sx={{
                  borderBottom: '2px solid #635bff',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  pl: 2,
                }}
              >
                Material Details & Total Cost
              </Typography>
              <Box pl={2} mt={1}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    '@media print': {
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'nowrap',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                    <strong>Material Details:</strong> {ticket.ConsMat}
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2, pr: '20px' }}>
                    <strong>Total Cost:</strong>  ₹{parseFloat(ticket.TotalCost || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box mb={0.3} p={0.2} border="1px solid #e0e0e0" borderRadius={1}>
              <Typography
                variant="h6"
                color="primary"
                sx={{
                  borderBottom: '2px solid #635bff',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  pl: 2,
                }}
              >
                Description & Resolve Remark
              </Typography>
              <Box pl={2} mt={1}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    '@media print': {
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      textAlign: 'left',
                      flexWrap: 'nowrap',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2, textAlign: 'left', }}>
                    <strong>Description:</strong> {ticket.Description}
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2, pr: '20px', mt: 1, textAlign: 'left', }}>
                    <strong>Resolve Remark:</strong>  {ticket.ResRemark}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box mb={0.3} p={0.2} border="1px solid #e0e0e0" borderRadius={1}>
              <Typography
                variant="h6"
                color="primary"
                sx={{
                  borderBottom: '2px solid #635bff',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  pl: 2,
                }}
              >
                Contact Information
              </Typography>
              <Box pl={2} mt={1}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    '@media print': {
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'nowrap',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                    <strong>Mobile:</strong> {ticket.MobileNo}
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.2, pr: '20px' }}>
                    <strong>Raised By:</strong> {ticket.raisedBy || ticket.Name}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box mb={0.3} p={0.2} border="1px solid #e0e0e0" borderRadius={1} sx={{
              display: 'flex',
              flexWrap: 'wrap',
              mt: 1,
              justifyContent: 'space-between',
              '@media print': {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'nowrap',
              },
            }}>
              <Typography
                sx={{
                  fontSize: '1rem', lineHeight: 1.2, pr: '0px', pl: 2
                }}
              >
                <strong>  Remark :</strong> {ticket.Remark}
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderTop: '1px dashed #ddd',
                fontSize: '0.8rem',
                color: '#777',
              }}
            >
              Thank you for your patience
            </Box>
          </DialogContent>
        </Box>
      </Dialog>

      <Dialog open={followUpDialogOpen} onClose={handleFollowUpCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 1, px: 3, display: 'flex', alignItems: 'center' }}>
          <RouteIcon sx={{ mr: 1 }} color='secondary' />
          <Typography variant="h6" color='secondary'>Follow Up</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} md={12} lg={12}>
              <TextField
                id='ToDo'
                name="ToDo"
                fullWidth
                multiline
                rows={1}
                variant='filled'
                label="Todo"
                className="custom-textfield"
                value={formData.ToDo}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <TextField
                id="Remark"
                name="Remark"
                fullWidth
                multiline
                rows={1}
                variant='filled'
                label="Remark"
                className="custom-textfield"
                value={formData.Remark}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12} lg={12} mt={1} display="flex" justifyContent="flex-end">
              <Button size='small'
                variant="contained"
                onClick={handleSubmit}
                sx={{ marginRight: 1 }}
              >
                Submit
              </Button>
              <Button size='small'
                variant="outlined"
                color='error'
                onClick={handleFollowUpCancel}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};
export default TicketDetailsPopup;
